// Magnetic Particle Testing (MT) Types

export type MTSurfaceCondition = string
export type MTStatus = 'Accepted' | 'Not Accepted'
export type MTIndicatorType = 'fluorescent' | 'visible'
export type MTMpiType = 'mpi' | 'fluorescent'
/** Legacy tablet value `'standard'` is treated as `'mpi'` in the PDF. */
export type MPITestType = MTMpiType | 'standard'
export type MTMagnetizationType = 'circular' | 'longitudinal' | 'multidirectional'
export type MTMaterialType = string

export const MT_TEST_SITES = ['Bergen', 'Mongstad', 'Ågotnes'] as const
export type MTTestSite = (typeof MT_TEST_SITES)[number]
export const MT_DEFAULT_TEST_SITE: MTTestSite = 'Bergen'

// MT Equipment Settings
export interface MTEquipmentSettings {
  magnetizingEquipment: string
  lighting: string
  demagnetization: string
  otherEquipment: string
  current: string
  fieldStrength: string
  magneticInk: string
  inkBatchNo: string
  contrast: string
  contrastBatchNo: string
}

export type MTConsumablePreset = Pick<
  MTEquipmentSettings,
  'lighting' | 'magneticInk' | 'inkBatchNo' | 'contrast' | 'contrastBatchNo'
>

export const MT_CONSUMABLE_PRESETS: Record<MTMpiType, MTConsumablePreset> = {
  mpi: {
    lighting: '1100 LUX',
    magneticInk: 'MR76',
    inkBatchNo: '100562020323',
    contrast: 'MR-72',
    contrastBatchNo: '1898398',
  },
  fluorescent: {
    lighting: '1500µW/Cm2 / 10 LUX',
    magneticInk: 'Fluxo 6',
    inkBatchNo: 'L231106-1',
    contrast: 'UV – Light, 365 Nm',
    contrastBatchNo: 'M3Zb16332',
  },
}

export function applyMpiConsumables(
  equipment: MTEquipmentSettings,
  mpiType: MTMpiType,
): MTEquipmentSettings {
  return { ...equipment, ...MT_CONSUMABLE_PRESETS[mpiType] }
}

export function normalizeMpiType(mpiType?: string): MTMpiType {
  return mpiType === 'fluorescent' ? 'fluorescent' : 'mpi'
}

// Inspector interface for MT reports
export interface MTInspector {
  id: string
  first_name: string
  last_name: string
  certification_number?: string
  mt_level?: number
  user_id?: string
}

// Company interface for MT reports
export interface MTCompany {
  id: string
  name: string
  report_code: string | null
}

// Re-export general NDT settings types for backwards compatibility
export type { CompanyNDTSettings as CompanyMTSettings } from './ndtSettings.types'
export type { CompanyNDTSettingsDetailed } from './ndtSettings.types'
export type { NDTTestProcedure, NDTTestAcceptance } from './ndtSettings.types'

export interface MTReportData {
  testDate: string
  testSite?: string
  reportNumber: string
  objects: string[]
  objectDescription: string
  issuedBy: string
  verifiedBy?: string
  status: MTStatus
  surfaceCondition: MTSurfaceCondition
  equipmentSettings: MTEquipmentSettings
  certificationNumber: string
  sequenceNumber: string
  extentOfExamination: string
  material: MTMaterialType
  mpiType?: MPITestType
  drawing?: string
  diameter?: string
  thickness?: string
  customerProject?: string
  hullYard?: string
  charge?: string
  testProcedure?: string
  testAcceptance?: string
  referenceStandards?: string
  comments?: string
  company?: string
  report_code?: string | null
  attachedImage?: string
  approvedBy?: string
  approvedAt?: string
  useCoil?: boolean
}

export interface MTFormData {
  testDate: string
  testSite: string
  reportNumber: string
  objects: string[]
  objectDescription: string
  issuedBy: string
  verifiedBy?: string
  status: MTStatus
  surfaceCondition: MTSurfaceCondition
  equipmentSettings: MTEquipmentSettings
  extentOfExamination: string
  extentOfExaminationType: 'spec' | 'percentage'
  material: MTMaterialType
  mpiType: MTMpiType
  drawing?: string
  diameter?: string
  thickness?: string
  customerProject?: string
  hullYard?: string
  charge?: string
  testProcedure?: string
  testAcceptance?: string
  referenceStandards?: string
  comments?: string
  company?: string
  report_code?: string | null
  attachedImage?: string
  useCoil?: boolean
}

export interface MTReportOptions {
  includeEquipmentSettings: boolean
  includeExtentOfExamination: boolean
  includeSignatures: boolean
  templateVersion: string
  companyLogo?: string
}

export const DEFAULT_MT_EQUIPMENT: MTEquipmentSettings = {
  magnetizingEquipment: 'Yoke, Magnaflux Y-1, sn: 2291',
  demagnetization: 'No',
  otherEquipment: 'Castrol Strips Type A (type II / Silver)',
  current: 'AC / 220v',
  fieldStrength: '4,75 Kg – 15 cm',
  ...MT_CONSUMABLE_PRESETS.mpi,
}

export const DEFAULT_STANDARD_MT_EQUIPMENT = DEFAULT_MT_EQUIPMENT

export const FLUORESCENT_SURFACE_CONDITIONS = ['Cleaned', 'Machined', 'Forged'] as const

export const STANDARD_SURFACE_CONDITIONS = [
  'Welded, normal',
  'Welded, fine',
  'Welded, grinded',
  'Sandblasted, normal',
  'Sandblasted, fine',
  'Machined',
  'Grinded',
  'Forged',
  'Cast',
] as const

export const STANDARD_MT_PROCEDURES = [
  'MT DNV-CG-0051 in accordance with ISO 17638',
  'MT in accordance with M101',
  'MT in accordance with M601',
  'MT for Cameron',
  'MT for FMC in accordance with ISO 17638',
  'ISO 17638',
  'ISO 4986',
  'EN 10228-1 Class 4',
  'ASTM E 709',
  'ASME Section V Article 7',
  'ASTM E 1444',
  'A275/A275M Practice for Magnetic Particle Examination of Steel Forgings',
  'A456/A456M Specification for Magnetic Particle Examination of Large Crankshaft Forgings',
  'API RP 7G-2',
  'TH Hill DS-1',
  'Fernley-Procter NS-2',
  'EN 10228-1 (steel forgings)',
  'ASTM A275 / A275M (steel forgings)',
  'MT in accordance with EN-13480-5',
  'MT WIN-TR-0054',
]

export function getAutoSelectedProcedure(surfaceCondition: MTSurfaceCondition): string | null {
  if (surfaceCondition.startsWith('Welded')) return 'ISO 17638'
  if (surfaceCondition === 'Cast') return 'ISO 4986'
  if (surfaceCondition === 'Forged') return 'EN 10228-1 Class 4'
  return null
}


